import { useQuoteToCustomer } from "@/common/api/mutation";
import { useGenerateFormElements } from "@/common/hooks";
import { FormFieldsStrategyType, QuotesType } from "@/common/types";
import { Button, Form, Popover, Tag } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import VendorsActions from "../QuoteActions/VendorsActions";
import { noBidReasonOptions } from "@/common/const/filter-options";
import { usePermissions } from "@/common/contexts/Permissions.context";
import { ChangeVendorModal } from "@/components/Modals";
import { useModal } from "@/common/contexts/Modal.context";
import vendorQuoteLineStrategy from "@/common/strategies/quoteLines/vendor/vendorQuoteLine.strategy";
import quotesRender from "@/common/const/elementsList/quotesRender.const";
import CustomTypography from "@/components/Typography/CustomTypography";
import { QuoteLineType } from "@/common/types/quote/quoteLine.type";
import { Quote } from "@/common/types/quote/quote.type";
import VendorQuoteLineStatusInfo from "./VendorQuoteLineStatusInfo";

export default function VendorQuoteLineForm({
  productType,
  productData,
  quoteData,
}: {
  productType: QuotesType | undefined;
  productData: QuoteLineType;
  quoteData: Quote;
}) {
  const {isHavePermission} = usePermissions()
  const navigate = useNavigate();
  const { openModal } = useModal();
  const quoteLineId = useParams().quoteLineId;
  const [vendorFormStrategy, setVendorFormStrategy] = useState<
    FormFieldsStrategyType[]
  >([]);
  const [noBidReason, setNoBidReason] = useState<null | string>(null);
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [form] = Form.useForm();
  const { formElements } = useGenerateFormElements(vendorFormStrategy);
  const { company } = usePermissions();
  const { mutate: submitQuote } = useQuoteToCustomer();
  useEffect(() => {
    if (!productData?.price) return;
    const formDefaultValue = Object.entries(productData.price).reduce(
      (acc, [key, value]) => {
        return {
          ...acc,
          [key]: value === null ? "" : value && value.toString(),
        };
      }
    );
    setVendorFormStrategy(
      vendorQuoteLineStrategy[productType as QuotesType].map((el) => {
        if (quoteData.status !== "Pending") {
          return { ...el, readonly: true };
        }
        return el;
      })
    );
    return form.setFieldsValue({
      ...formDefaultValue,
      description: productData?.vendorDescription || "",
      quoteType: productData?.quoteType,
    });
  }, [productData]);
  const externalSubmit = (noBidReason: string) => {
    form.submit();
    setNoBidReason(noBidReason);
  };
  const changePopover = () => {
    setPopoverOpen((state) => !state);
  };
  const onFinish = (values: any) => {
    const dataToSubmit = {
      quoteId: quoteData.id,
      data: {
        quoteLineType: values.quoteType || "Firm",
        vendorId: quoteData?.implementer?.id,
        customerId: quoteData.initiator.id,
        price: values,
        description: values.description || null,
        quoteLineId: quoteLineId,
        method: noBidReason ? "bid" : "price",
        ...(noBidReason && { noBidReason }),
      },
    };

    return submitQuote(dataToSubmit, {
      onSuccess: () => {
        toast.success(
          noBidReason ? "Quote submitted to customer" : "Quote saved"
        );
        navigate(-1);
      },
    });
  };

  const onVendorChangeModal = () => {
    return openModal(
      <ChangeVendorModal
        vendor={quoteData?.implementer}
        quoteId={quoteData?.id}
      />
    );
  };
  const pricing = [...formElements.slice(0, 4)];
  const productDataElements = [...formElements.slice(4, -1)];
  return (
    <>
      <Form form={form} onFinish={onFinish} disabled={!isHavePermission('quotes_vendor_edit')}>
        <div className="w-full">
          <div className="bg-white rounded-lg p-5 w-full">
            <div className="flex justify-between items-center">
              <div className="flex gap-2 items-center ">
                <CustomTypography>
                  {quotesRender.find((el) => el.value === productType)?.label}
                </CustomTypography>
                <Tag className="text-primary font-kanit text-[13px] leading-[13px] flex items-center justify-center border-none bg-primaryDim py-2">
                  Pricing
                </Tag>
              </div>
              <div>
                <VendorQuoteLineStatusInfo
                  status={quoteData.status}
                  noBidReason={productData.noBidReason}
                />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">{...pricing}</div>
          </div>
        </div>

        <div className=" bg-white p-5 mt-5 rounded-lg">
          <div className="grid grid-cols-4 gap-4">{...productDataElements}</div>
          <div className="mt-1">{formElements[formElements.length - 1]}</div>
        </div>
        <div className="flex gap-5 items-center justify-between mt-5">
          <div className="flex gap-5 items-center">
            <VendorsActions
              approvedByVendor={productData.approvedByVendor}
              changePopover={changePopover}
              noBidReason={productData.noBidReason}
              status={quoteData.status}
            />
          </div>
          {company && company.companyType === "airx" && (
            <Button
              className="h-10 font-inter text-base font-semibold"
              type="primary"
              htmlType="button"
              onClick={onVendorChangeModal}
              disabled={!isHavePermission("quotes_vendor_edit")}
            >
              Change vendor
            </Button>
          )}
        </div>
      </Form>
      <Popover
        open={popoverOpen}
        placement="topLeft"
        destroyTooltipOnHide
        trigger="click"
        onOpenChange={changePopover}
        content={
          <div className="flex flex-col -m-4">
            {noBidReasonOptions.map((el) => (
              <button
                onClick={() => externalSubmit(el.value)}
                className="rounded-none first:rounded-t-lg last:rounded-b-lg border last:border-none py-2 bg-white px-3"
              >
                {el.label}
              </button>
            ))}
          </div>
        }
      />
    </>
  );
}
